
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { hapticFeedback } from "@/utils/haptics";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-none text-sm font-medium transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-win95-blue text-win95-white border border-win95-black shadow-win95 hover:shadow-win95-pressed active:shadow-win95-pressed",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "bg-win95-gray text-win95-text border border-win95-black shadow-win95 hover:shadow-win95-pressed active:shadow-win95-pressed",
        secondary:
          "bg-win95-gray text-win95-text border border-win95-black shadow-win95 hover:shadow-win95-pressed active:shadow-win95-pressed",
        ghost: "hover:bg-accent hover:text-accent-foreground border-0",
        link: "text-primary underline-offset-4 hover:underline border-0",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-none px-3",
        lg: "h-11 rounded-none px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  hapticIntensity?: 'light' | 'medium' | 'heavy';
  disableHaptics?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, hapticIntensity = 'medium', disableHaptics = false, onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    
    const handleClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
      if (!disableHaptics) {
        // Determine haptic intensity based on variant if not explicitly set
        let intensity = hapticIntensity;
        if (variant === 'destructive') {
          intensity = 'heavy';
        } else if (variant === 'ghost' || variant === 'link') {
          intensity = 'light';
        }
        
        hapticFeedback[intensity]();
      }
      
      onClick?.(event);
    }, [hapticIntensity, disableHaptics, onClick, variant]);

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onClick={handleClick}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
